import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Layers, Wand2, ScanSearch, ShieldOff, LayoutList, Fingerprint,
  Sparkles, ChevronRight, CheckCircle,
} from 'lucide-react';

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.07, duration: 0.45 } }),
};

const EXAMPLE_LENSES = [
  'NLP / Meta Model Lens',
  'ICF Coaching Lens',
  'Counselling Reflection Lens',
  'CBT-Informed Lens',
  'ACT-Informed Lens',
  'Somatic / Embodiment Lens',
  'Hypnotherapy Lens',
  'Leadership Coaching Lens',
  'Your Own Custom Lens',
];

const FEATURE_CARDS = [
  {
    icon: Layers,
    title: 'Choose Your Lens',
    body: 'Select from pre-built analysis lenses such as NLP, ICF Coaching, Counselling Reflection, CBT-Informed, ACT-Informed, Somatic, Hypnotherapy, or Leadership Coaching.',
  },
  {
    icon: Wand2,
    title: 'Create Your Own',
    body: 'Build a custom analysis lens based on your own method, model, or practitioner framework.',
  },
  {
    icon: ScanSearch,
    title: 'Decide What AI Looks For',
    body: 'Tell Session Intelligence™ what to pay attention to — beliefs, values, emotional patterns, body-based language, cognitive distortions, parts conflicts, identity statements, or action commitments.',
  },
  {
    icon: ShieldOff,
    title: 'Decide What AI Avoids',
    body: 'Guide the AI away from language or assumptions you do not want — diagnosis, clinical labels, NLP terminology, therapy language, certainty claims, or anything outside your scope.',
  },
  {
    icon: LayoutList,
    title: 'Custom Report Sections',
    body: 'Choose the report sections that matter most to your work — key themes, client language observations, practitioner reflections, suggested questions, next-session focus, or follow-up prompts.',
  },
  {
    icon: Fingerprint,
    title: 'Your Method, Your Language',
    body: 'The goal is not to force you into one model. The goal is to help Session Intelligence™ work through your method.',
  },
];

export function SICustomLensPage() {
  const [interested, setInterested] = useState(false);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-5 via-white to-success-5 p-6 lg:p-10">
      <div className="max-w-4xl mx-auto space-y-12">

        {/* Header */}
        <motion.div
          variants={fadeUp} initial="hidden" animate="visible" custom={0}
          className="space-y-3"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-success-10 border border-success-20 rounded-full text-xs font-semibold text-success-60 uppercase tracking-wide">
            <Sparkles className="w-3.5 h-3.5" />
            Coming Soon
          </div>
          <h1 className="text-3xl lg:text-4xl font-bold text-gray-80 leading-tight">
            Custom Lens<sup className="text-[60%] align-super">™</sup>
          </h1>
          <p className="text-xl font-medium text-gray-60 leading-snug">
            Analyse sessions through your own coaching or practitioner framework
          </p>
          <p className="text-base text-gray-50 leading-relaxed max-w-2xl">
            Session Intelligence<sup className="text-[65%] align-super">™</sup> is built to give you structured AI-assisted session analysis. Custom Lens<sup className="text-[65%] align-super">™</sup> will allow you to guide that analysis through the way you actually work.
          </p>
        </motion.div>

        {/* Body copy */}
        <motion.div
          variants={fadeUp} initial="hidden" animate="visible" custom={1}
          className="bg-white rounded-2xl border border-gray-15 p-6 lg:p-8 space-y-4"
        >
          <p className="text-gray-60 leading-relaxed">
            Not every coach, counsellor, hypnotherapist, practitioner, or leadership consultant works from the same model.
          </p>
          <ul className="space-y-1.5 text-gray-50 text-sm">
            {['Some use NLP.', 'Some use ICF-style coaching.', 'Some use CBT-informed work.', 'Some use ACT.', 'Some use somatic or embodiment-based approaches.', 'Some use counselling reflection.', 'Some use their own framework.'].map(line => (
              <li key={line} className="flex items-start gap-2">
                <ChevronRight className="w-4 h-4 text-success-40 flex-shrink-0 mt-0.5" />
                {line}
              </li>
            ))}
          </ul>
          <p className="text-gray-60 leading-relaxed pt-2">
            Custom Lens<sup className="text-[65%] align-super">™</sup> will allow you to choose or create the lens you want Session Intelligence<sup className="text-[65%] align-super">™</sup> to use when reviewing a client session. The AI will analyse the client's actual language, patterns, emotional signals, contradictions, and session content through your preferred approach — while still keeping the core safety rules in place.
          </p>
        </motion.div>

        {/* Feature cards */}
        <div>
          <motion.h2
            variants={fadeUp} initial="hidden" animate="visible" custom={2}
            className="text-lg font-bold text-gray-70 mb-5"
          >
            What Custom Lens<sup className="text-[65%] align-super">™</sup> Will Include
          </motion.h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {FEATURE_CARDS.map((card, i) => (
              <motion.div
                key={card.title}
                variants={fadeUp} initial="hidden" animate="visible" custom={i + 3}
                className="bg-white rounded-2xl border border-gray-15 p-5 space-y-3 hover:shadow-sm transition-shadow"
              >
                <div className="p-2 bg-success-10 rounded-xl w-fit">
                  <card.icon className="w-4 h-4 text-success-50" />
                </div>
                <p className="text-sm font-semibold text-gray-80">{card.title}</p>
                <p className="text-xs text-gray-50 leading-relaxed">{card.body}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Example lenses */}
        <motion.div
          variants={fadeUp} initial="hidden" animate="visible" custom={9}
          className="space-y-4"
        >
          <h2 className="text-lg font-bold text-gray-70">
            Example Custom Lenses
          </h2>
          <div className="flex flex-wrap gap-2">
            {EXAMPLE_LENSES.map((lens, i) => (
              <span
                key={lens}
                className={`px-3 py-1.5 rounded-full text-sm font-medium border ${
                  i === EXAMPLE_LENSES.length - 1
                    ? 'bg-success-10 border-success-20 text-success-60'
                    : 'bg-white border-gray-15 text-gray-60'
                }`}
              >
                {lens}
              </span>
            ))}
          </div>
        </motion.div>

        {/* Safety note */}
        <motion.div
          variants={fadeUp} initial="hidden" animate="visible" custom={10}
          className="bg-amber-50 border border-amber-200 rounded-2xl p-5"
        >
          <p className="text-xs text-amber-700 leading-relaxed">
            <span className="font-semibold">Important: </span>
            Custom Lens<sup className="text-[65%] align-super">™</sup> will allow practitioners to guide the analysis through their preferred framework, but it will not override the core safety rules. Session Intelligence<sup className="text-[65%] align-super">™</sup> provides coaching and practitioner hypotheses, not diagnosis, therapy, medical advice, legal advice, or mental health assessment.
          </p>
        </motion.div>

        {/* CTA */}
        <motion.div
          variants={fadeUp} initial="hidden" animate="visible" custom={11}
          className="bg-gradient-to-br from-success-50 to-success-60 rounded-2xl p-8 text-center text-white space-y-4"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/20 rounded-full text-xs font-semibold uppercase tracking-wide">
            Coming Soon
          </div>
          <h2 className="text-2xl font-bold">Custom Lens<sup className="text-[60%] align-super">™</sup> is currently in preview</h2>
          <p className="text-white/80 text-sm max-w-md mx-auto leading-relaxed">
            We are testing this feature with early users before building the full version. If this would be useful to you, let us know.
          </p>
          {interested ? (
            <div className="inline-flex items-center gap-2 px-5 py-3 bg-white rounded-xl text-success-60 font-semibold text-sm">
              <CheckCircle className="w-4 h-4" />
              Thanks — we've registered your interest in Custom Lens<sup className="text-[65%] align-super">™</sup>
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <button
                onClick={() => setInterested(true)}
                className="px-6 py-3 bg-white text-success-60 font-semibold text-sm rounded-xl hover:bg-white/90 transition-colors"
              >
                I'm Interested in Custom Lens<sup className="text-[65%] align-super">™</sup>
              </button>
              <button
                onClick={() => setInterested(true)}
                className="px-6 py-3 bg-white/20 hover:bg-white/30 text-white font-medium text-sm rounded-xl transition-colors border border-white/30"
              >
                Request This Feature
              </button>
            </div>
          )}
        </motion.div>

      </div>
    </div>
  );
}
